import { Loader2, Trash2, Tag, CheckCircle2, Copy, Receipt } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function NoteDialog({ open, onOpenChange, editingNote, noteForm, setNoteForm, saveNote }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-xl font-bold">{editingNote ? "Modifier la note" : "Nouvelle note"}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <Input placeholder="Titre" value={noteForm.title} onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="client-note-title" />
          <Textarea placeholder="Contenu" value={noteForm.content} onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })} rows={4} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
          <Select value={noteForm.category} onValueChange={(v) => setNoteForm({ ...noteForm, category: v })}>
            <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#14101c] border-white/10 text-white">
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="important">Important</SelectItem>
              <SelectItem value="suivi">Suivi</SelectItem>
              <SelectItem value="action">Action</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={saveNote} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="save-client-note-btn">
            {editingNote ? "Modifier" : "Creer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AuditDialog({ open, onOpenChange, editingAudit, auditForm, setAuditForm, saveAudit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-xl font-bold">{editingAudit ? "Modifier l'audit" : "Nouvel audit"}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <Input placeholder="Titre" value={auditForm.title} onChange={(e) => setAuditForm({ ...auditForm, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="client-audit-title" />
          <Textarea placeholder="Contenu" value={auditForm.content} onChange={(e) => setAuditForm({ ...auditForm, content: e.target.value })} rows={5} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
          <Select value={auditForm.status} onValueChange={(v) => setAuditForm({ ...auditForm, status: v })}>
            <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#14101c] border-white/10 text-white">
              <SelectItem value="en_cours">En cours</SelectItem>
              <SelectItem value="termine">Termine</SelectItem>
              <SelectItem value="en_attente">En attente</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Recommandations (une par ligne)" value={auditForm.recommendations} onChange={(e) => setAuditForm({ ...auditForm, recommendations: e.target.value })} rows={3} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
          <Button onClick={saveAudit} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="save-client-audit-btn">
            {editingAudit ? "Modifier" : "Creer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EnrollDialog({ open, onOpenChange, formations, enrollFormationId, setEnrollFormationId, handleEnroll }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-xl font-bold">Inscrire a une formation</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <Select value={enrollFormationId} onValueChange={setEnrollFormationId}>
            <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue placeholder="Choisir une formation..." /></SelectTrigger>
            <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
              {formations.map(f => <SelectItem key={f.formation_id} value={f.formation_id}>{f.title} ({f.code})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleEnroll} disabled={!enrollFormationId} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">Inscrire</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirmDialog({ open, onOpenChange, userName, handleDeleteClient, deleting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle className="text-xl font-bold text-red-400">Supprimer ce client ?</DialogTitle></DialogHeader>
        <div className="mt-4">
          <p className="text-sm text-slate-400 mb-4">Cette action est irreversible. Toutes les donnees du client seront supprimees.</p>
          <p className="text-sm font-medium mb-6">Client : <strong>{userName}</strong></p>
          <div className="flex gap-3">
            <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1 border-white/10 text-white hover:bg-white/5">Annuler</Button>
            <Button onClick={handleDeleteClient} disabled={deleting} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-delete-client-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AssignStaffDialog({ open, onOpenChange, allStaff, assignments, assignStaffId, setAssignStaffId, handleAssignStaff }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-xl font-bold">Attribuer un membre</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <Select value={assignStaffId} onValueChange={setAssignStaffId}>
            <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue placeholder="Choisir un membre..." /></SelectTrigger>
            <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
              {allStaff.filter(s => !assignments.some(a => a.staff_id === s.user_id)).map(s => (
                <SelectItem key={s.user_id} value={s.user_id}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ color: s.role === "coach" ? "#3b82f6" : s.role === "sous_traitant" ? "#10b981" : "#f7c935" }}>
                      {s.role === "coach" ? "Coach" : s.role === "sous_traitant" ? "ST" : "Admin"}
                    </span>
                    {s.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleAssignStaff} disabled={!assignStaffId} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">Attribuer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddServiceDialog({ open, onOpenChange, catalogServices, docTemplates, selectedCatalog, setSelectedCatalog, selectedIntakeTemplate, setSelectedIntakeTemplate, handleAddServiceFromCatalog }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-xl font-bold">Attribuer un service</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-xs text-slate-500">Selectionnez un service cree dans le Builder pour l'attribuer a ce client.</p>
          {catalogServices.length === 0 ? (
            <div className="bg-[#161020] border border-white/5 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-400">Aucun service dans le catalogue.</p>
              <p className="text-xs text-slate-500 mt-1">Creez vos services dans <strong>Builder &gt; Services Catalogue</strong>.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Service du catalogue</label>
                <select value={selectedCatalog} onChange={e => setSelectedCatalog(e.target.value)}
                  className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="catalog-service-select">
                  <option value="">Choisir un service...</option>
                  {catalogServices.map(cs => (
                    <option key={cs.catalog_id} value={cs.catalog_id}>{cs.name} {cs.category ? `(${cs.category})` : ""}</option>
                  ))}
                </select>
              </div>
              {selectedCatalog && (
                <div className="bg-[#161020] rounded-lg p-3 text-sm">
                  <div className="text-slate-300">{catalogServices.find(c => c.catalog_id === selectedCatalog)?.description}</div>
                  {(catalogServices.find(c => c.catalog_id === selectedCatalog)?.price || catalogServices.find(c => c.catalog_id === selectedCatalog)?.base_price) && (
                    <div className="mt-1 font-bold" style={{ color: "#f7c935" }}>{catalogServices.find(c => c.catalog_id === selectedCatalog)?.price || catalogServices.find(c => c.catalog_id === selectedCatalog)?.base_price}</div>
                  )}
                </div>
              )}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Document a faire remplir par le client</label>
                <p className="text-[10px] text-slate-600 mb-1">Le client verra ce formulaire dans son espace Services et pourra le remplir.</p>
                <select value={selectedIntakeTemplate} onChange={e => setSelectedIntakeTemplate(e.target.value)}
                  className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="intake-template-select">
                  <option value="">Aucun document requis</option>
                  {docTemplates.map(dt => (
                    <option key={dt.template_id} value={dt.template_id}>{dt.name}</option>
                  ))}
                </select>
              </div>
              <Button onClick={handleAddServiceFromCatalog} disabled={!selectedCatalog} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-add-service-btn">
                Attribuer le service
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FormViewDialog({ open, onOpenChange, viewingForm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-xl font-bold">{viewingForm?.template_name || "Formulaire"}</DialogTitle></DialogHeader>
        {viewingForm && (
          <div className="space-y-4 mt-4">
            <div className="text-xs text-slate-500">Soumis le {new Date(viewingForm.created_at).toLocaleDateString("fr")}</div>
            {Object.entries(viewingForm.responses || {}).map(([key, val]) => (
              <div key={key} className="bg-[#161020] rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">{key}</div>
                <div className="text-sm text-white">{typeof val === "object" ? JSON.stringify(val) : String(val) || "-"}</div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AssignAccDialog({ open, onOpenChange, accTemplates, selectedAccTemplate, setSelectedAccTemplate, handleAssignAccompaniment }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-xl font-bold">Attribuer un accompagnement</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-xs text-slate-500">Selectionnez un modele cree dans le Builder pour l'attribuer a ce client.</p>
          {accTemplates.length === 0 ? (
            <div className="bg-[#161020] border border-white/5 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-400">Aucun modele d'accompagnement.</p>
              <p className="text-xs text-slate-500 mt-1">Creez des modeles dans <strong>Builder &gt; Modeles Accompagnement</strong>.</p>
            </div>
          ) : (
            <>
              <select value={selectedAccTemplate} onChange={e => setSelectedAccTemplate(e.target.value)}
                className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="acc-template-select">
                <option value="">Choisir un modele...</option>
                {accTemplates.map(t => (
                  <option key={t.template_id} value={t.template_id}>{t.title || t.name} {t.formula ? `(${t.formula})` : ""}</option>
                ))}
              </select>
              {selectedAccTemplate && (() => {
                const tpl = accTemplates.find(t => t.template_id === selectedAccTemplate);
                return tpl ? (
                  <div className="bg-[#161020] rounded-lg p-3 text-sm space-y-1">
                    <div className="text-slate-300">{tpl.description}</div>
                    <div className="flex gap-4 text-xs text-slate-400">
                      {tpl.formula && <span>Formule : <strong className="text-white">{tpl.formula}</strong></span>}
                      {tpl.duration_months && <span>Duree : <strong className="text-white">{tpl.duration_months} mois</strong></span>}
                      {tpl.price && <span>Prix : <strong style={{ color: "#f7c935" }}>{tpl.price} EUR</strong></span>}
                    </div>
                    {tpl.phases && <div className="text-xs text-slate-500 mt-1">{tpl.phases.length} phases</div>}
                  </div>
                ) : null;
              })()}
              <Button onClick={handleAssignAccompaniment} disabled={!selectedAccTemplate}
                className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-assign-acc-btn">
                Attribuer l'accompagnement
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TagsDialog({ open, onOpenChange, tags }) {
  const [copiedTag, setCopiedTag] = useState(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Tag className="w-5 h-5 text-[#f7c935]" /> Balises disponibles</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 mt-2">Copiez ces balises pour les utiliser dans vos templates, devis, contrats et emails.</p>
        <div className="space-y-4 mt-4">
          {[
            { title: "Client", keys: Object.keys(tags).filter(k => k.startsWith("client.")) },
            { title: "Fiche de renseignement", keys: Object.keys(tags).filter(k => k.startsWith("fiche.")) },
            { title: "Entreprise (Blitzz)", keys: Object.keys(tags).filter(k => k.startsWith("entreprise.")) },
            { title: "Dates", keys: Object.keys(tags).filter(k => k.startsWith("date.")) },
          ].map(group => group.keys.length > 0 && (
            <div key={group.title}>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">{group.title}</h4>
              <div className="grid md:grid-cols-2 gap-1.5">
                {group.keys.map(key => (
                  <button key={key} onClick={() => { navigator.clipboard.writeText(`{{${key}}}`); setCopiedTag(key); setTimeout(() => setCopiedTag(null), 2000); }}
                    className="flex items-center gap-2 p-2.5 rounded-lg bg-[#0b0811] border border-white/5 hover:border-[#f7c935]/30 text-left transition-colors group"
                    data-testid={`tag-${key}`}>
                    <code className="text-xs text-[#f7c935] font-mono flex-shrink-0">{`{{${key}}}`}</code>
                    <span className="text-xs text-slate-400 flex-1 truncate">{tags[key] || "-"}</span>
                    {copiedTag === key ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" /> : <Copy className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceDetailDialog({ viewingInvoice, setViewingInvoice, invoicePayments, api }) {
  const navigate = useNavigate();
  if (!viewingInvoice) return null;
  const d = viewingInvoice;
  const typeLabel = { devis: "Devis", facture: "Facture", avoir: "Avoir" }[d.type] || d.type;
  const sMap = { brouillon: { l: "Brouillon", c: "bg-yellow-500/10 text-yellow-400" }, envoye: { l: "Envoyee", c: "bg-blue-500/10 text-blue-400" }, signe: { l: "Signee", c: "bg-purple-500/10 text-purple-400" }, paye: { l: "Payee", c: "bg-green-500/10 text-green-400" }, impaye: { l: "Impayee", c: "bg-orange-500/10 text-orange-400" }, en_retard: { l: "En retard", c: "bg-red-500/10 text-red-400" }, annule: { l: "Annulee", c: "bg-slate-500/10 text-slate-400" } };
  const st = sMap[d.status] || { l: d.status, c: "bg-slate-500/10 text-slate-400" };

  return (
    <Dialog open={!!viewingInvoice} onOpenChange={(o) => { if (!o) setViewingInvoice(null); }}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-3">
            <Receipt className="w-5 h-5 text-[#f7c935]" />
            {typeLabel} {d.reference}
            <span className={`text-xs px-2 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2" data-testid="invoice-detail-dialog">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-[#0b0811] rounded-lg p-3"><span className="text-slate-500 block mb-0.5">Client</span><span className="font-medium">{d.client_name}</span></div>
            <div className="bg-[#0b0811] rounded-lg p-3"><span className="text-slate-500 block mb-0.5">Date emission</span><span className="font-medium">{d.issue_date || "-"}</span></div>
            <div className="bg-[#0b0811] rounded-lg p-3"><span className="text-slate-500 block mb-0.5">Echeance</span><span className="font-medium">{d.due_date || "-"}</span></div>
            <div className="bg-[#0b0811] rounded-lg p-3"><span className="text-slate-500 block mb-0.5">Conditions</span><span className="font-medium">{d.payment_conditions || "-"}</span></div>
          </div>
          {d.lines?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-bold mb-2">Lignes</div>
              <div className="bg-[#0b0811] rounded-lg overflow-hidden">
                <div className="grid grid-cols-5 gap-2 text-[10px] text-slate-500 uppercase font-bold px-3 py-2 border-b border-white/5">
                  <span className="col-span-2">Description</span><span>Qte</span><span>P.U. HT</span><span className="text-right">TTC</span>
                </div>
                {d.lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 text-xs px-3 py-2 border-b border-white/[0.03]">
                    <span className="col-span-2 text-white">{l.description}</span>
                    <span className="text-slate-400">{l.quantity}</span>
                    <span className="text-slate-400">{parseFloat(l.unit_price || 0).toFixed(2)}</span>
                    <span className="text-right font-medium text-white">{parseFloat(l.total_ttc || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <div className="bg-[#0b0811] rounded-lg p-4 min-w-[200px] space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-400"><span>Total HT</span><span>{(d.total_ht || 0).toFixed(2)} EUR</span></div>
              <div className="flex justify-between text-slate-400"><span>TVA</span><span>{(d.total_tva || 0).toFixed(2)} EUR</span></div>
              <div className="flex justify-between font-black text-[#f7c935] text-base pt-1 border-t border-white/10"><span>Total TTC</span><span>{(d.total_ttc || 0).toFixed(2)} EUR</span></div>
            </div>
          </div>
          {invoicePayments.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-bold mb-2">Paiements</div>
              <div className="space-y-1.5">
                {invoicePayments.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 bg-[#0b0811] rounded-lg text-xs">
                    <span className="text-green-400 font-bold">{parseFloat(p.amount || 0).toFixed(2)} EUR</span>
                    <span className="text-slate-400">{p.method || ""}</span>
                    <span className="text-slate-500 ml-auto">{p.date ? new Date(p.date).toLocaleDateString("fr-FR") : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.notes && <div className="text-xs text-slate-400 bg-[#0b0811] rounded-lg p-3"><span className="text-slate-500 block mb-1 font-bold">Notes</span>{d.notes}</div>}
          <div className="flex gap-2 justify-end pt-2">
            <Button onClick={() => { setViewingInvoice(null); navigate("/admin/facturation"); }} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="goto-facturation-btn">
              Ouvrir dans Facturation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
